/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { ERROR, QUOTED, PRICE_SELECTED } from 'pu-data/QuoteSteps';
import { parameterForceRequote } from 'pu-data/Utils';
import SimpleReactValidator from 'pu-utils/simple-react-validator';

import initialState from '../data/initialState';
import { getQuote, emailQuote } from 'api/apiQuote';

export const doQuote = createAsyncThunk(
  'mxAssembly/doQuote',
  async (args, thunkAPI) => {
    const state = thunkAPI.getState().mx_assembly;
    const { parameters } = state;

    const token = document.getElementsByName('authenticity_token')[0].value;
    const variant = document.getElementsByName('variant_id')[0].value;

    const promise = getQuote({
      data: parameters,
      quoteNumber: parameters.product_quote_number,
      token,
      variant,
    });

    return promise
      .then((res) => res.data)
      .catch((error) => thunkAPI.rejectWithValue({ error: error.message }));
  },
);

export const doEmailQuote = createAsyncThunk(
  'mxAssembly/doEmailQuote',
  async (args, thunkAPI) => {
    const { data, token } = args;
    const promise = emailQuote({ data, token });
    return promise
      .then((res) => res.data)
      .catch((error) => thunkAPI.rejectWithValue({ error: error.message }));
  });

export const validator = new SimpleReactValidator({});

/**
 * Returns an updated parameters object without mutating the original state
 */
const updateValue = (name, value, p) => {
  const parameters = { ...p };
  switch (name) {
    case 'qty1':
      parameters['quantities'][0] = value;
      break;
    case 'qty2':
      parameters['quantities'][1] = value;
      break;
    case 'qty3':
      parameters['quantities'][2] = value;
      break;
    default:
      parameters[name] = value;
  }

  return parameters;
};

const mxAssemblySlice = createSlice({
  name: 'mxAssembly',
  initialState: {
    ...initialState,
    errors: []
  },
  reducers: {
    changeParameter(state, action) {
      const { name, value } = action.payload
      state.parameters = updateValue(name, value, state.parameters);
      state.parametersChanged = parameterForceRequote(name);
    },
    priceSelected(state, action) {
      const sp = action.payload;
      state.parameters.row_selected = sp.row_selected;
      state.parameters.column_selected = sp.column_selected;
      state.parameters.quote_config_key = sp.quote_config_key;
      state.parameters.supplier_selected = sp.supplier;

      state.quoteStep = PRICE_SELECTED
    },
    // Accepts an array of field names to show error messages for.
    // If no fields are passed, shows all error messages
    // This updates the state to show the error messages and rerenders the component
    showValidatorErrorMessages(state, action) {
      const validatedFields = action.payload;

      if (validatedFields && validatedFields.length > 0) {
        validatedFields.forEach((name) => {
          validator.showMessageFor(name);
        });
      } else {
        validator.showMessages();
      }

      state.showMessages = true;
    },
    hideValidatorErrorMessages(state) {
      state.showMessages = false;
    },
    addErrors(state, action) {
      if (typeof action.payload === 'string') {
        state.errors = [action.payload];
      } else if (Array.isArray(action.payload)) {
        state.errors = action.payload;
      } else if (action.payload.errors) {
        state.errors = action.payload.errors;
      }
    },
    dismissErrors(state) {
      state.errors = []
    }
  },
  extraReducers(builder) {
    builder
      .addCase(doQuote.pending, (state) => {
        state.loading = true;
      })
      .addCase(doQuote.fulfilled, (state, action) => {
        const quote = action.payload;
        state.quote = quote;
        state.loading = false;
        state.parametersChanged = false;
        state.parameters.product_quote_number = quote.number;
        state.parameters.row_selected = '';
        state.parameters.column_selected = '';
        state.parameters.quote_config_key = '';
        state.parameters.supplier_selected = '';
        state.parameters.vendor_selected = '';

        if (!quote || !quote.line_items || quote.line_items.length <= 0) {
          state.quoteStep = ERROR;
        } else {
          state.quoteStep = QUOTED;
        }

        if (!quote || !quote.us_line_item_groups) {
          state.usGroups = [];
        } else {
          state.usGroups = quote.us_line_item_groups;
        }

        if (!quote || !quote.us_line_item_combined) {
          state.usCombined = {};
        } else {
          state.usCombined = quote.us_line_item_combined;
        }
      })
      .addCase(doQuote.rejected, (state) => {
        state.loading = false;
        state.quoteStep = ERROR;
        state.quote = {}
        state.usGroups = [];
        state.usCombined = {};
      })
      .addCase(doEmailQuote.pending, (state) => {
        state.loading = true;
      })
      .addCase(doEmailQuote.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(doEmailQuote.rejected, (state, action) => {
        state.loading = false;
      })
  },
});

export const {
  changeParameter,
  priceSelected,
  showValidatorErrorMessages,
  hideValidatorErrorMessages,
  addErrors,
  dismissErrors,
} = mxAssemblySlice.actions

export default mxAssemblySlice.reducer;
